import React, { useState, useEffect } from "react";
import {
  Grid,
  Stack,
  IconButton,
  Button,
  Typography,
  InputAdornment,
  CircularProgress,
  Paper,
  Alert,
  Box,
  Divider,
} from "@mui/material";
import {
  registerForm,
  setAuthenticated,
  uploadKtpJp,
  setSnackbarOpen,
  newUploadKtp,
  getRef,
  getRefSap,
  refEthnic,
  refCountries,
  refReligion,
  getRefRegion,
  uploadFileJp,
} from "src/redux/actions";
import localforage from "localforage";
import { useNavigate } from "react-router";
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  UploadFile,
  SwapHoriz,
} from "@mui/icons-material";
import {
  inputAlphabetOnly,
  inputNumberOnly,
} from "src/functions/inputNumberOnly";
import CustomInput from "./CustomInput";
import { version } from "src/configs/globalVariable";
import LoadableImage from "./LoadableImage";
import { CustomAutoComplete } from "src/pages/admin/Setting/EditPanel/ComponentGroup";
import { isPama } from "src/functions/isInstitute";
import CustomDatePicker from "src/components/CustomDatePicker";

// const OCR_THRESHOLD = 87.5;
const OCR_THRESHOLD = 0;

function compareNumbers(input1, input2) {
  const str1 = input1?.toString();
  const str2 = input2?.toString();
  if (!str1 || !str2) return 0;

  let matchingCount = 0;
  for (let i = 0; i < Math.min(str1.length, str2.length); i++) {
    if (str1[i] === str2[i]) matchingCount++;
  }

  return (matchingCount / Math.max(str1.length, str2.length)) * 100;
}

const styleGrid = {
  p: 2,
  backgroundColor: "#ffff",
  m: "auto",
  borderRadius: "0.75rem",
  boxShadow: "rgba(0, 0, 0, 0.09) 0px 3px 12px",
};

const Register = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConf, setShowPasswordConf] = useState(false);
  const [form, setForm] = useState({});
  const [validate, setValidate] = useState(false);
  const [loading, setLoading] = useState("container");
  const [ocrPercentage, setOcrPercentage] = useState(0);
  const [ocrResult, setOcrResult] = useState("");
  const [pathKtp, setPathKtp] = useState("");
  const [ktpFileName, setKtpFileName] = useState("");
  const [religions, setReligions] = useState([]);
  const [birthCities, setBirthCities] = useState([]);
  const [refGender, setRefGender] = useState([]);
  const [maritalStatus, setMaritalStatus] = useState([]);
  const [fileKtp, setFileKtp] = useState(null);

  const baseURL =
    process.env.NODE_ENV !== "production"
      ? "http://localhost:5000/"
      : window.location.origin + "/";

  useEffect(() => {
    fetchRef();
  }, []);

  const fetchRef = async () => {
    const resEmail = await localforage.getItem("email");
    if (!resEmail) navigate("/");

    const resRef = await Promise.all([
      getRefSap({ type: "sapReligion" }),
      getRefSap({ type: "sapMaritalStatus" }),
      getRefSap({ type: "sapBirthplace" }),
      getRef({ type: "refGender" }),
    ]);
    setReligions(resRef[0].data);
    setMaritalStatus(resRef[1].data);
    setBirthCities(resRef[2].data);
    setRefGender(resRef[3].data);

    setForm((prev) => ({ ...prev, email: resEmail }));
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    // Hitung ulang persentase saat user edit NIK manual
    if (name === "identity_number") {
      const resCompare = compareNumbers(value, ocrResult);
      setOcrPercentage(resCompare || 0);
    }
  };

  const handleSubmit = async () => {
    setValidate(true);

    const hasEmptyField =
      !form.email ||
      !form.identity_number ||
      !form.fullname ||
      !form.password ||
      !form.password_conf ||
      !form.mobile_phone ||
      !form.address_ktp ||
      !form.religion_id ||
      !form.birth_place ||
      !form.birth_date ||
      !form.gender_code ||
      !form.marital_status_id;

    if (hasEmptyField) return;
    if (form.password !== form.password_conf) return;
    if (ocrPercentage < OCR_THRESHOLD) return;

    const payload = {
      ...form,
      identity_path: pathKtp,
      identity_ocr_result: ocrResult,
      identity_ocr_percentage: ocrPercentage,
      address_ktp: form.address_ktp,
      sap_religion_id: form.religion_id,
      sap_marital_status_id: form.marital_status_id,
    };

    setLoading("buttonRegist");
    const resRegist = await registerForm({ data_personal: payload });
    await uploadFileJp(fileKtp, form.identity_number, "ktp");
    setLoading("");

    if (resRegist.code === 200) {
      setAuthenticated(resRegist.data.token);
      navigate(`/${version}/user/`);
    } else {
      setSnackbarOpen("error", resRegist.message);
    }
  };

  const handleUpload = async (files) => {
    if (!files.length) return;

    setLoading("uploadKtp");
    const file = files[0];
    const resUpload = await uploadKtpJp(file);

    if (typeof resUpload === "string") {
      setLoading("");
      setSnackbarOpen("error", resUpload);
      return;
    }

    const {
      nik,
      nama,
      raw,
      tempatLahir,
      tanggalLahir,
      jenisKelamin,
      alamat,
      agama,
      statusPerkawinan,
      preview,
    } = resUpload.data;

    setPathKtp(preview || "");
    setKtpFileName(file.name);
    setOcrResult(nik || "");

    let gender = undefined;
    let birthCity = undefined;
    let marital = undefined;
    let religion = undefined;

    if (jenisKelamin) {
      const found = refGender.find(
        (r) =>
          r.gender_name?.toLowerCase()?.trim() ===
          jenisKelamin?.toLowerCase()?.trim(),
      );

      if (found) gender = found;
    }

    if (tempatLahir) {
      const found = birthCities.find(
        (r) =>
          r.id?.toLowerCase()?.trim() === tempatLahir?.toLowerCase()?.trim(),
      );

      if (found) birthCity = found;
    }

    if (statusPerkawinan) {
      let mappedStatus = statusPerkawinan?.toLowerCase()?.trim();

      // Map OCR status to maritalStatus object
      if (mappedStatus === "kawin") {
        mappedStatus = "Married";
      } else if (mappedStatus === "belum kawin") {
        mappedStatus = "Single";
      }

      const found = maritalStatus.find(
        (r) =>
          r.id?.toLowerCase()?.trim() === mappedStatus?.toLowerCase()?.trim(),
      );

      if (found) marital = found;
    }

    if (agama) {
      const found = religions.find(
        (r) => r.id?.toLowerCase()?.trim() === agama?.toLowerCase()?.trim(),
      );

      if (found) religion = found;
    }

    // Auto-fill form dari hasil OCR
    const convertDateFormat = (dateStr) => {
      if (!dateStr) return "";
      const [day, month, year] = dateStr.split("-");
      return `${year}-${month}-${day}`;
    };

    setForm((prev) => ({
      ...prev,
      identity_number: nik || "",
      fullname: nama || prev.fullname || "",
      tempatLahir: tempatLahir || prev.tempatLahir || "",
      tanggalLahir: tanggalLahir || prev.tanggalLahir || "",
      jenisKelamin: jenisKelamin || prev.jenisKelamin || "",
      alamat: alamat || prev.alamat || "",
      address_ktp: alamat || prev.address_ktp || "",
      agama: agama || prev.agama || "",
      statusPerkawinan: statusPerkawinan || prev.statusPerkawinan || "",

      gender_code: gender?.gender_code || prev.gender_code || "",
      gender: gender || prev.gender || "",
      birth_place: birthCity?.id || prev.birth_place || "",
      birth_date: tanggalLahir
        ? convertDateFormat(tanggalLahir)
        : prev.birth_date || "",
      marital_status_id: marital?.id || prev.marital_status_id || "",
      religion_id: religion?.id || prev.religion_id || "",
      religion: religion || prev.religion || "",
    }));

    // Saat fresh upload, persentase langsung 100%
    setOcrPercentage(nik ? 100 : 0);
    setLoading("");
    setLoading("container");
    setFileKtp(file);
    setTimeout(() => {
      setLoading("");
    }, 300);
  };

  const handleGantiKtp = () => {
    setPathKtp("");
    setKtpFileName("");
    setOcrResult("");
    setOcrPercentage(0);
    setForm((prev) => ({
      ...prev,
      identity_number: "",
      fullname: "",
      agama: "",
      statusPerkawinan: "",
    }));
    setValidate(false);
  };

  const handleSelectAuto = async (val, key) => {
    setForm((prev) => {
      if (key === "gender" || key === "gender_code") {
        return {
          ...prev,
          gender_code: val?.gender_code || "",
          gender: val,
        };
      }

      if (key === "religion") {
        return {
          ...prev,
          religion_id: val["id"],
          religion: val,
        };
      }

      if (key === "birth_place") {
        return {
          ...prev,
          birth_place: val["id"],
        };
      }

      return {
        ...prev,
        [key]: val?.[key] ?? val,
      };
    });
  };

  const handleDateChange = (val, key) => {
    setForm({ ...form, [key]: val });
  };

  // ─── Loading container ───────────────────────────────────────
  if (loading === "container") {
    return (
      <Stack>
        <CircularProgress sx={{ m: "auto" }} />
      </Stack>
    );
  }

  const isOcrValid = ocrPercentage >= OCR_THRESHOLD;
  const isKtpUploaded = !!pathKtp;

  return (
    <Grid maxWidth="md" sx={styleGrid}>
      {/* Header */}
      <Stack sx={{ width: "100%", p: 2, mt: 2 }}>
        <Typography sx={{ fontSize: "24px" }}>
          <b>Registrasi</b>
        </Typography>
        <Alert severity="warning" sx={{ mt: "1rem" }}>
          Mohon untuk dapat mengisi data-data awal yang diminta dengan VALID.
          Jika terdapat pemalsuan/kebohongan data, maka PAMA dapat
          menindaklanjuti sesuai dengan peraturan perundang-undangan yang
          berlaku serta peraturan perusahaan.
        </Alert>
      </Stack>

      <Grid item container>
        {/* ─── Kolom Kanan: Upload KTP ─────────────────────────── */}
        <Grid
          item
          xs={12}
          sm={5}
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Step label */}
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "0.08em",
              color: "text.secondary",
              mb: 1,
            }}
          >
            LANGKAH 1 — UPLOAD KTP
          </Typography>

          {/* Preview area */}
          <Paper
            variant="outlined"
            sx={{
              width: "100%",
              height: "180px",
              mb: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              borderStyle: isKtpUploaded ? "solid" : "dashed",
              borderColor: isKtpUploaded ? "success.main" : "divider",
              borderRadius: "0.75rem",
              backgroundColor: isKtpUploaded ? "transparent" : "grey.50",
            }}
          >
            {isKtpUploaded ? (
              <LoadableImage
                // src={`${baseURL}${pathKtp}`}
                src={`${pathKtp}`}
                alt="KTP"
                style={{ width: "100%", height: "100%" }}
              />
            ) : (
              <Stack alignItems="center" spacing={1}>
                <UploadFile sx={{ fontSize: 36, color: "text.disabled" }} />
                <Typography sx={{ fontSize: "13px", color: "text.disabled" }}>
                  Foto KTP belum diupload
                </Typography>
              </Stack>
            )}
          </Paper>

          {/* Info file setelah upload */}
          {isKtpUploaded && ktpFileName && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1,
                px: 1,
              }}
            >
              <CheckCircle sx={{ fontSize: 16, color: "success.main" }} />
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "text.secondary",
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {ktpFileName}
              </Typography>
            </Box>
          )}

          {/* Tombol upload / ganti */}
          {!isKtpUploaded ? (
            <Button
              variant="contained"
              component="label"
              disabled={loading === "uploadKtp"}
              startIcon={
                loading === "uploadKtp" ? (
                  <CircularProgress size={16} />
                ) : (
                  <UploadFile />
                )
              }
              fullWidth
            >
              {loading === "uploadKtp"
                ? "Memproses..."
                : "Upload Kartu Identitas"}
              <input
                hidden
                accept="image/*"
                type="file"
                onChange={(e) => handleUpload(e.target.files)}
              />
            </Button>
          ) : (
            <Button
              variant="outlined"
              component="label"
              disabled={loading === "uploadKtp"}
              startIcon={<SwapHoriz />}
              fullWidth
              onClick={handleGantiKtp}
            >
              Ganti KTP
              <input
                hidden
                accept="image/*"
                type="file"
                onChange={(e) => handleUpload(e.target.files)}
              />
            </Button>
          )}

          {/* Status OCR */}
          {isKtpUploaded && (
            <Alert
              severity={isOcrValid ? "success" : "error"}
              sx={{ mt: 1, fontSize: "12px" }}
            >
              {isOcrValid ? (
                <>
                  KTP terdeteksi. Data diisi otomatis —{" "}
                  <b>periksa dan edit jika perlu.</b>
                </>
              ) : (
                <>
                  NIK tidak cocok dengan KTP. Pastikan nomor KTP yang diinput
                  sesuai dengan foto yang diupload.
                </>
              )}
            </Alert>
          )}

          {/* Hint kalau belum upload */}
          {!isKtpUploaded && (
            <Alert severity="info" sx={{ mt: 1, fontSize: "12px" }}>
              Upload KTP terlebih dahulu. Data NIK dan nama akan otomatis terisi
              dari foto KTP.
            </Alert>
          )}
        </Grid>

        {/* ─── Kolom Kiri: Form Data Diri ──────────────────────── */}
        {/* ─── Kolom Kiri: Form Data Diri ──────────────────────── */}
        <Grid item xs={12} sm={7} sx={{ p: 2 }}>
          {/* Step label */}
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "0.08em",
              color: isKtpUploaded ? "text.secondary" : "text.disabled",
              mb: 1,
            }}
          >
            LANGKAH 2 — LENGKAPI DATA DIRI
          </Typography>

          {/* Overlay hint kalau belum upload KTP */}
          {!isKtpUploaded && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Upload foto KTP terlebih dahulu untuk mengisi data diri.
            </Alert>
          )}

          <Box
            sx={{
              opacity: isKtpUploaded ? 1 : 0.4,
              pointerEvents: isKtpUploaded ? "auto" : "none",
            }}
          >
            <Grid container spacing={2}>
              {/* Email */}
              <Grid item xs={12}>
                <CustomInput label="Email" disabled value={form.email || ""} />
              </Grid>

              {/* Section: Data KTP */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} textAlign="left">
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "text.secondary",
                    }}
                  >
                    DATA SESUAI KTP
                  </Typography>
                </Divider>
              </Grid>

              <Grid item xs={12} sm={6}>
                <CustomInput
                  label="Nomor KTP (16 digit)"
                  name="identity_number"
                  onChange={handleChange}
                  onKeyDown={inputNumberOnly}
                  value={form.identity_number || ""}
                  error={validate && !form.identity_number}
                  helperText={
                    form.identity_number &&
                    form.identity_number.length !== 16 ? (
                      <span style={{ color: "red" }}>* Harus 16 digit</span>
                    ) : form.identity_number?.length === 16 ? (
                      <span style={{ color: "green" }}>✓ Terisi dari KTP</span>
                    ) : (
                      validate &&
                      !form.identity_number &&
                      "* Nomor KTP harap diisi"
                    )
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <CustomInput
                  label="Nama Lengkap (Sesuai KTP)"
                  name="fullname"
                  onChange={handleChange}
                  value={form.fullname || ""}
                  error={validate && !form.fullname}
                  helperText={
                    form.fullname ? (
                      <span style={{ color: "green" }}>✓ Terisi dari KTP</span>
                    ) : (
                      validate && !form.fullname && "* Nama lengkap harap diisi"
                    )
                  }
                  onKeyDown={inputAlphabetOnly}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                {/* <CustomInput
                  label="Tempat Lahir"
                  name="tempatLahir"
                  onChange={handleChange}
                  value={form.tempatLahir || ""}
                  error={validate && !form.tempatLahir}
                  helperText={
                    form.tempatLahir ? (
                      <span style={{ color: "green" }}>✓ Terisi dari KTP</span>
                    ) : (
                      validate &&
                      !form.tempatLahir &&
                      "* Tempat lahir harap diisi"
                    )
                  }
                /> */}
                <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                  Tempat Lahir
                </Typography>
                <CustomAutoComplete
                  name="birth_place"
                  optionsArr={birthCities}
                  keyLabel={"id"}
                  handleSelect={handleSelectAuto}
                  value={
                    birthCities?.filter(
                      (bc) => bc["id"] === form?.birth_place,
                    )[0]
                  }
                  style={{ marginBottom: "1rem" }}
                  error={validate && !form.birth_place}
                  helperText={
                    form.tempatLahir ? (
                      <span style={{ color: "green" }}>✓ Terisi dari KTP</span>
                    ) : (
                      validate &&
                      !form.tempatLahir &&
                      "* Tempat lahir harap diisi"
                    )
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                {/* <CustomInput
                  label="Tanggal Lahir"
                  name="tanggalLahir"
                  onChange={handleChange}
                  value={form.tanggalLahir || ""}
                  error={validate && !form.tanggalLahir}
                  helperText={
                    form.tanggalLahir ? (
                      <span style={{ color: "green" }}>✓ Terisi dari KTP</span>
                    ) : (
                      validate &&
                      !form.tanggalLahir &&
                      "* Tanggal lahir harap diisi"
                    )
                  }
                /> */}
                <CustomDatePicker
                  label="Tanggal Lahir"
                  name="birth_date"
                  onChange={handleDateChange}
                  value={form?.birth_date}
                  error={validate && !form.birth_date}
                  helperText={
                    form.tanggalLahir ? (
                      <span style={{ color: "green" }}>✓ Terisi dari KTP</span>
                    ) : (
                      validate &&
                      !form.tanggalLahir &&
                      "* Tanggal lahir harap diisi"
                    )
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                {/* <CustomInput
                  label="Jenis Kelamin"
                  name="jenisKelamin"
                  onChange={handleChange}
                  value={form.jenisKelamin || ""}
                  error={validate && !form.jenisKelamin}
                  helperText={
                    form.jenisKelamin ? (
                      <span style={{ color: "green" }}>✓ Terisi dari KTP</span>
                    ) : (
                      validate &&
                      !form.jenisKelamin &&
                      "* Jenis kelamin harap diisi"
                    )
                  }
                /> */}
                <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                  Jenis Kelamin
                </Typography>
                <CustomAutoComplete
                  name="gender_code"
                  optionsArr={refGender}
                  keyLabel={"gender_name"}
                  handleSelect={handleSelectAuto}
                  value={
                    refGender.filter(
                      (g) => g.gender_code === form.gender_code,
                    )[0]
                  }
                  style={{ marginBottom: "1rem" }}
                  error={validate && !form.gender_code}
                  helperText={
                    form.jenisKelamin ? (
                      <span style={{ color: "green" }}>✓ Terisi dari KTP</span>
                    ) : (
                      validate &&
                      !form.jenisKelamin &&
                      "* Jenis kelamin harap diisi"
                    )
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                {/* <CustomInput
                  label="Status Perkawinan"
                  name="statusPerkawinan"
                  onChange={handleChange}
                  value={form.statusPerkawinan || ""}
                  error={validate && !form.statusPerkawinan}
                  helperText={
                    form.statusPerkawinan ? (
                      <span style={{ color: "green" }}>✓ Terisi dari KTP</span>
                    ) : (
                      validate &&
                      !form.statusPerkawinan &&
                      "* Status perkawinan harap diisi"
                    )
                  }
                /> */}
                <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                  Status Pernikahan
                </Typography>
                <CustomAutoComplete
                  name="marital_status_id"
                  optionsArr={maritalStatus}
                  keyLabel={"id"}
                  handleSelect={handleSelectAuto}
                  value={
                    maritalStatus.filter(
                      (m) => m["id"] === form.marital_status_id,
                    )[0]
                  }
                  style={{ marginBottom: "1rem" }}
                  error={validate && !form.marital_status_id}
                  helperText={
                    form.statusPerkawinan ? (
                      <span style={{ color: "green" }}>✓ Terisi dari KTP</span>
                    ) : (
                      validate &&
                      !form.statusPerkawinan &&
                      "* Status perkawinan harap diisi"
                    )
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                {/* <CustomInput
                  label="Agama"
                  name="agama"
                  onChange={handleChange}
                  value={form.agama || ""}
                  error={validate && !form.agama}
                  helperText={
                    form.agama ? (
                      <span style={{ color: "green" }}>✓ Terisi dari KTP</span>
                    ) : (
                      validate && !form.agama && "* Agama harap diisi"
                    )
                  }
                /> */}
                <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                  Agama
                </Typography>
                <CustomAutoComplete
                  name="religion"
                  optionsArr={religions}
                  keyLabel={"id"}
                  handleSelect={handleSelectAuto}
                  value={religions.filter((e) => e.id === form.religion_id)[0]}
                  style={{ marginBottom: "1rem" }}
                  error={validate && !form["religion_id"]}
                  helperText={
                    form.agama ? (
                      <span style={{ color: "green" }}>✓ Terisi dari KTP</span>
                    ) : (
                      validate && !form.agama && "* Agama harap diisi"
                    )
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                {/* <CustomInput
                  label="Alamat"
                  name="alamatKtp"
                  onChange={handleChange}
                  value={form.alamatKtp || ""}
                  error={validate && !form.alamatKtp}
                  helperText={
                    form.alamatKtp ? (
                      <span style={{ color: "green" }}>✓ Terisi dari KTP</span>
                    ) : (
                      validate && !form.alamatKtp && "* Alamat harap diisi"
                    )
                  }
                /> */}
                <CustomInput
                  label="Alamat Lengkap"
                  multiline
                  rows={4}
                  name="address_ktp"
                  onChange={handleChange}
                  value={form?.address_ktp}
                  error={validate && !form.address_ktp}
                  helperText={
                    form.alamat ? (
                      <span style={{ color: "green" }}>✓ Terisi dari KTP</span>
                    ) : (
                      validate && !form.alamat && "* Alamat harap diisi"
                    )
                  }
                />
              </Grid>

              {/* Section: Akun & Keamanan */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} textAlign="left">
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "text.secondary",
                    }}
                  >
                    AKUN & KEAMANAN
                  </Typography>
                </Divider>
              </Grid>

              <Grid item xs={12} sm={6}>
                <CustomInput
                  label="Kata Sandi"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  onChange={handleChange}
                  value={form.password || ""}
                  error={validate && !form.password}
                  helperText={
                    validate && !form.password && "* Kata sandi harap diisi"
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                        >
                          {showPassword ? (
                            <Visibility color="primary" />
                          ) : (
                            <VisibilityOff />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <CustomInput
                  label="Konfirmasi Kata Sandi"
                  name="password_conf"
                  type={showPasswordConf ? "text" : "password"}
                  onChange={handleChange}
                  value={form.password_conf || ""}
                  error={validate && !form.password_conf}
                  helperText={
                    <Stack
                      direction="column"
                      sx={{
                        color: "#d32f2f",
                        fontSize: "12px",
                        lineHeight: "18px",
                      }}
                    >
                      <Typography sx={{ fontSize: "inherit" }}>
                        {validate &&
                          !form.password_conf &&
                          "* Konfirmasi kata sandi harap diisi"}
                      </Typography>
                      <Typography sx={{ fontSize: "inherit" }}>
                        {validate &&
                          form.password &&
                          form.password_conf &&
                          form.password !== form.password_conf &&
                          "* Password tidak sesuai"}
                      </Typography>
                    </Stack>
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPasswordConf(!showPasswordConf)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                        >
                          {showPasswordConf ? (
                            <Visibility color="primary" />
                          ) : (
                            <VisibilityOff />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <CustomInput
                  label="Nomor Handphone"
                  name="mobile_phone"
                  onChange={handleChange}
                  value={form.mobile_phone || ""}
                  error={validate && !form.mobile_phone}
                  helperText={
                    validate &&
                    !form.mobile_phone &&
                    "* Nomor handphone harap diisi"
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>

      {/* Footer Actions */}
      <Stack
        direction="row"
        spacing={2}
        justifyContent="end"
        sx={{ my: "1rem", pr: 2, width: "100%" }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            navigate(-1);
            localforage.removeItem("email");
          }}
        >
          Batal
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            !isKtpUploaded ||
            form?.identity_number?.length !== 16 ||
            loading === "buttonRegist" ||
            ocrPercentage < OCR_THRESHOLD
          }
          startIcon={
            loading === "buttonRegist" ? <CircularProgress size={16} /> : null
          }
        >
          {loading === "buttonRegist" ? "Memproses..." : "Kirim"}
        </Button>
      </Stack>
    </Grid>
  );
};

export default Register;
